import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiBaseOrigin } from '../../../../shared/config/api-origin';
import { PlatformHeader } from '../../../../shared/platform/PlatformHeader';
import '../../../overview/ui/overview.css';
import {
  createHistoricalAdapter,
  historicalQuery,
  HistoricalRequestError,
} from '../../adapters/historical/http';
import type {
  HistoricalFilter,
  HistoricalGroup,
  HistoricalPageResult,
  HistoricalPort,
  HistoricalRow,
  HistoricalRun,
} from '../../consumer/historical/contract';
import {
  HISTORICAL_METHODS,
  HISTORICAL_ROOT,
  historicalHref,
  historicalSourceLabel,
  parseHistoricalLocation,
} from '../../consumer/historical/contract';
import { externalHref } from '../../consumer/historical/external-contract';
import { riskHref } from '../../consumer/workbench';
import '../management/management.css';
import '../normal-monitoring.css';
import './historical.css';
const numeric = (v: number | null) => (v === null ? '不可评估' : String(v));
function safeUrl(url: string): string | undefined {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && !u.username && !u.password ? u.href : undefined;
  } catch {
    return undefined;
  }
}
interface ReadState<T> {
  status: 'LOADING' | 'READY' | 'ERROR';
  data: T | null;
  error: unknown;
}
function useHistoricalRead<T>(read: (signal: AbortSignal) => Promise<T>) {
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState<ReadState<T>>({ status: 'LOADING', data: null, error: null });
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ status: 'LOADING', data: null, error: null });
    void read(controller.signal).then(
      (data) => {
        if (active) setState({ status: 'READY', data, error: null });
      },
      (error) => {
        if (active) setState({ status: 'ERROR', data: null, error });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [read, retry]);
  return { state, retry: () => setRetry((n) => n + 1) };
}
export function HistoricalReadNotice({
  status,
  error,
  retry,
}: {
  status: ReadState<unknown>['status'];
  error: unknown;
  retry: () => void;
}) {
  if (status === 'READY') return null;
  return (
    <section className="nm-historical-notice" role="status">
      <p>
        {status === 'LOADING'
          ? '正在读取已保存的离线实验…'
          : error instanceof HistoricalRequestError && error.status === 404
            ? '404 · 未找到已保存的实验。请核对 runId 或返回实验列表。'
            : '历史实验读取失败：服务离线、存储不可用或响应格式无效。结果未知；未使用演示数值替代。'}
      </p>
      {status === 'ERROR' && (
        <button type="button" onClick={retry}>
          重新读取历史实验
        </button>
      )}
    </section>
  );
}
export function HistoricalRunList({
  data,
  areaId,
}: {
  data: HistoricalPageResult<HistoricalRun>;
  areaId: string;
}) {
  return (
    <>
      <p>
        共 {data.total} 个已保存实验 · 当前 {data.items.length} 个
      </p>
      {!data.items.length && (
        <p className="nm-empty">
          没有匹配的已保存历史实验。真实数据可能尚未取得或回测尚未导入；当前预测效用不可推断。
        </p>
      )}
      <div className="nm-historical-runs">
        {data.items.map((r) => (
          <article className="nm-management-card" key={r.runId} data-historical-run={r.runId}>
            <h2>{historicalSourceLabel(r.dataset.sourceType)}</h2>
            <p>
              {r.dataset.sources.map((s) => s.stationId).join(' / ')} · {r.dataset.resolution} ·{' '}
              {r.dataset.metrics.join(' / ')}
            </p>
            <p>
              {r.dataset.start} → {r.dataset.end}
            </p>
            <p>
              工程运行状态 {r.status} · Predictive Utility: <strong>{r.predictiveUtility}</strong>
            </p>
            <p>
              +{r.horizonsHours.join(' / +')} 小时 · {r.methods.length} 种独立小时方法
            </p>
            <p className="nm-provenance">
              runId {r.runId} · 创建 {r.createdAt}
            </p>
            <a href={historicalHref(r.runId, undefined, areaId)}>
              查看冻结实验、基准比较与原始误差 →
            </a>
          </article>
        ))}
      </div>
    </>
  );
}
export function HistoricalProvenance({ run }: { run: HistoricalRun }) {
  const d = run.dataset;
  const s = run.split;
  return (
    <section className="nm-management-card" data-historical-provenance>
      <h2>数据来源与冻结边界</h2>
      <p>
        <strong>{historicalSourceLabel(d.sourceType)}</strong> · {run.mode} · {d.resolution}
      </p>
      <p>
        数据集 {d.datasetId} · 修订 {d.datasetRevision}
        <br />
        {d.start} → {d.end} · 访问 {d.accessedAt}
      </p>
      <p>
        许可 {d.license || '未知'} · DOI {d.doi || '未知'}{' '}
        {d.doi && <a href={safeUrl(`https://doi.org/${d.doi}`)}>来源引用</a>}
      </p>
      <p className="nm-provenance">
        归一化快照 SHA256 {d.snapshotHash}
        <br />
        不可变行账本 SHA256 {run.rowsHash} · {run.rowCount} 行
      </p>
      {d.sources.map((source) => (
        <details key={source.stationId}>
          <summary>
            {source.stationId} · {source.name || '站名未知'} · 下载 {source.downloadedRows} / 合格{' '}
            {source.qualifiedRows} / 缺失 {source.missingRows} 行
          </summary>
          <p>
            经纬度 {source.latitude ?? '未知'} / {source.longitude ?? '未知'} · 下载 {source.bytes}{' '}
            字节
          </p>
          <p>
            <a href={safeUrl(source.url)}>{source.url}</a>
          </p>
          <p className="nm-provenance">原始 SHA256 {source.originalHash}</p>
        </details>
      ))}
      <div className="nm-historical-split">
        <p>
          TRAIN
          <br />
          {s.trainStart} → {s.trainEnd}
        </p>
        <p>
          VALIDATION
          <br />
          {s.validationStart} → {s.validationEnd}
        </p>
        <p>
          HELD_OUT
          <br />
          {s.heldoutStart} → {s.heldoutEnd}
        </p>
      </div>
      <p>
        边界隔离 {s.embargoHours} 小时 · 冻结 {run.freeze.frozenAt}
      </p>
      <details>
        <summary>冻结模型、特征参数与质量诊断</summary>
        <p className="nm-provenance">
          模型冻结 {run.freeze.modelFreezeSHA}
          <br />
          算法 {run.freeze.algorithmSHA}
          <br />
          训练指纹 {run.freeze.trainingHash}
        </p>
        <pre>{run.freeze.parametersJson}</pre>
        {d.qualityNotes.map((n, i) => (
          <p key={i}>{n}</p>
        ))}
      </details>
      <p>
        历史档案经过事后汇集与质控；当时接收可用时间未知。小时回测不能证明秒级实时预测、当地消防阈值或火灾预测有效。
      </p>
    </section>
  );
}
function matches(g: HistoricalGroup, f: HistoricalFilter) {
  return (
    (!f.stationId || g.stationId === f.stationId) &&
    (!f.metric || g.metric === f.metric) &&
    (!f.method || g.method === f.method) &&
    (!f.horizonHours || g.horizonHours === Number(f.horizonHours))
  );
}
export function historicalViewFilter(
  run: HistoricalRun,
  filter: HistoricalFilter,
): HistoricalFilter {
  return {
    ...filter,
    stationId:
      filter.stationId || run.dataset.sources[0]?.stationId || run.groups[0]?.stationId || '',
    metric:
      filter.metric ||
      (run.dataset.metrics.includes('TEMPERATURE') ? 'TEMPERATURE' : run.dataset.metrics[0] || ''),
  };
}
export function historicalErrorFilter(
  row: HistoricalRow,
  filter: HistoricalFilter,
): HistoricalFilter {
  return {
    ...filter,
    stationId: row.stationId,
    metric: row.metric,
    method: row.method,
    horizonHours: String(row.horizonHours),
    from: row.targetAt,
    to: new Date(Date.parse(row.targetAt) + 3600000).toISOString(),
    offset: 0,
  };
}
export function historicalCasesFor(run: HistoricalRun, filter: HistoricalFilter): HistoricalRow[] {
  const scope = historicalViewFilter(run, filter);
  return run.errorCases.filter(
    (r) =>
      (!scope.stationId || r.stationId === scope.stationId) &&
      (!scope.metric || r.metric === scope.metric) &&
      (!scope.method || r.method === scope.method) &&
      (!scope.horizonHours || r.horizonHours === Number(scope.horizonHours)),
  );
}
export function HistoricalErrorCases({
  run,
  filter,
  navigate,
}: {
  run: HistoricalRun;
  filter: HistoricalFilter;
  navigate: (f: HistoricalFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [offset, setOffset] = useState(0);
  const rows = historicalCasesFor(run, filter);
  return (
    <section className="nm-management-card">
      <h2>已保存错误案例与限制</h2>
      <details data-historical-error-section onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary>
          查看当前分组 {rows.length} 个错误案例 / 实验完整保留 {run.errorCases.length}{' '}
          个（每页10个）
        </summary>
        <p>按当前站点、指标、时间目标和方法读取案例；切换分组可查全部不可变案例。</p>
        {open && (
          <>
            <div>
              {rows.slice(offset, offset + 10).map((r, i) => (
                <details
                  key={i}
                  data-historical-error-origin={r.origin}
                  data-historical-error-target={r.targetAt}
                >
                  <summary>
                    {r.stationId} / {r.metric} / +{r.horizonHours}h / {r.method} · {r.origin}
                  </summary>
                  <p>
                    target {r.targetAt} · {r.status} · {r.reason || '无已记录原因'}
                    <br />
                    预测 {numeric(r.predicted)} / 实际 {numeric(r.actual)} {r.unit}
                    <br />
                    basis {r.inputStart || '未知'} → {r.inputEnd || '未知'}
                  </p>
                  <button
                    type="button"
                    data-historical-error-origin={r.origin}
                    data-historical-error-target={r.targetAt}
                    onClick={() => navigate(historicalErrorFilter(r, filter))}
                  >
                    定位此起点的原始行
                  </button>
                </details>
              ))}
            </div>
            <div className="nm-pagination">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - 10))}
              >
                上一页案例
              </button>
              <span>第 {Math.floor(offset / 10) + 1} 页</span>
              <button
                type="button"
                disabled={offset + 10 >= rows.length}
                onClick={() => setOffset(offset + 10)}
              >
                下一页案例
              </button>
            </div>
            {!rows.length && <p>当前分组没有已保存错误案例；不能推断没有误差。</p>}
          </>
        )}
      </details>
      {run.limitations.map((l, i) => (
        <p key={i}>{l}</p>
      ))}
    </section>
  );
}
export function HistoricalGroups({
  run,
  filter,
  select,
  scope,
}: {
  run: HistoricalRun;
  filter: HistoricalFilter;
  select: (g: HistoricalGroup) => void;
  scope?: (filter: HistoricalFilter) => void;
}) {
  const [splitView, setSplitView] = useState('HELD_OUT');
  const viewFilter = historicalViewFilter(run, filter);
  const groups = run.groups.filter((g) => matches(g, viewFilter) && g.split === splitView);
  return (
    <section className="nm-management-card">
      <h2>冻结比较 · Predictive Utility: {run.predictiveUtility}</h2>
      <p>
        下列统计来自已保存的完整 split；查询时间筛选只缩小原始样本，不会重新评分或修改实验。各模型
        own 分母与四方法共同可评估 paired 分母分别展示。Δ = 候选 − LAST_VALUE：正数更差，负数更好。
      </p>
      <label>
        统计阶段{' '}
        <select
          aria-label="历史统计阶段"
          value={splitView}
          onChange={(e) => setSplitView(e.target.value)}
        >
          <option value="HELD_OUT">HELD_OUT 最终持有集</option>
          <option value="VALIDATION">VALIDATION 方法选择</option>
          <option value="TRAIN">TRAIN</option>
        </select>
      </label>
      <div className="nm-historical-scope-controls">
        <label>
          比较站点{' '}
          <select
            aria-label="历史比较站点"
            value={viewFilter.stationId}
            onChange={(e) => scope?.({ ...filter, stationId: e.target.value, offset: 0 })}
          >
            {run.dataset.sources.map((s) => (
              <option key={s.stationId} value={s.stationId}>
                {s.stationId} · {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          比较指标{' '}
          <select
            aria-label="历史比较指标"
            value={viewFilter.metric}
            onChange={(e) => scope?.({ ...filter, metric: e.target.value, offset: 0 })}
          >
            {run.dataset.metrics.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p>
        每次查看一个站点与一个指标；切换上方比较条件可查看所有已保存分组。当前 {groups.length}{' '}
        个统计组。
      </p>
      {!groups.length && <p>当前条件无已保存统计组，不能推断通过。</p>}
      <div
        className="nm-table-wrap"
        role="region"
        aria-label="历史实验数据表，可横向滚动"
        tabIndex={0}
      >
        <table className="nm-table">
          <caption>站点 × 指标 × 小时目标 × 方法 × split</caption>
          <thead>
            <tr>
              <th>分组与样本</th>
              <th>全部 / 生成 / 可评估</th>
              <th>Own MAE / RMSE / bias</th>
              <th>Coverage / paired 分母</th>
              <th>Paired MAE / RMSE</th>
              <th>相对基准 ΔMAE / ΔRMSE</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g, i) => (
              <tr
                key={i}
                data-historical-group={`${g.stationId}|${g.metric}|${g.horizonHours}|${g.method}|${g.split}`}
              >
                <td>
                  {g.stationId} / {g.metric} / {g.unit}
                  <small>
                    +{g.horizonHours}h · {g.method} · {g.split}
                  </small>
                  <button type="button" onClick={() => select(g)}>
                    展开此组样本
                  </button>
                </td>
                <td>
                  {g.totalOrigins} / {g.generated} / {g.evaluable}
                  <details>
                    <summary>不可评估原因</summary>
                    {g.unavailableReasons.length ? (
                      g.unavailableReasons.map((r, j) => (
                        <p key={j}>
                          {r.reason}: {r.count}
                        </p>
                      ))
                    ) : (
                      <p>无已记录缺口</p>
                    )}
                  </details>
                </td>
                <td>
                  <span
                    data-historical-field="mae"
                    data-value={g.mae === null ? 'null' : String(g.mae)}
                  >
                    {numeric(g.mae)}
                  </span>{' '}
                  /{' '}
                  <span
                    data-historical-field="rmse"
                    data-value={g.rmse === null ? 'null' : String(g.rmse)}
                  >
                    {numeric(g.rmse)}
                  </span>{' '}
                  /{' '}
                  <span
                    data-historical-field="bias"
                    data-value={g.bias === null ? 'null' : String(g.bias)}
                  >
                    {numeric(g.bias)}
                  </span>
                  <small>{g.unit}</small>
                </td>
                <td>
                  <span
                    data-historical-field="coverage"
                    data-value={g.coverage === null ? 'null' : String(g.coverage)}
                  >
                    {numeric(g.coverage)}
                  </span>{' '}
                  /{' '}
                  <span data-historical-field="pairedCount" data-value={String(g.pairedCount)}>
                    {g.pairedCount}
                  </span>
                </td>
                <td>
                  {numeric(g.pairedMAE)} / {numeric(g.pairedRMSE)}
                </td>
                <td>
                  {numeric(g.baselineMAEDelta)} / {numeric(g.baselineRMSEDelta)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
export function HistoricalLedgerCurve({ rows }: { rows: HistoricalRow[] }) {
  const evaluated = rows.filter((r) => r.predicted !== null || r.actual !== null);
  if (!evaluated.length) return <p>本页无可绘制数值；缺失不补零。</p>;
  const signature = (r: HistoricalRow) =>
    `${r.stationId}/${r.metric}/${r.horizonHours}/${r.method}/${r.split}`;
  if (new Set(evaluated.map(signature)).size !== 1)
    return <p>请选择一个比较分组查看曲线，避免混合不同单位、方法或 split。</p>;
  const sorted = [...evaluated].sort((a, b) => Date.parse(a.targetAt) - Date.parse(b.targetAt));
  const values = sorted.flatMap((r) =>
    [r.predicted, r.actual].filter((n): n is number => n !== null),
  );
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const t0 = Date.parse(sorted[0]!.targetAt);
  const t1 = Date.parse(sorted[sorted.length - 1]!.targetAt);
  const x = (r: HistoricalRow) => 50 + ((Date.parse(r.targetAt) - t0) / (t1 - t0 || 1)) * 820;
  const y = (v: number) => 190 - ((v - lo) / span) * 150;
  return (
    <figure className="nm-historical-curve">
      <figcaption>
        当前页的不可变 target 样本 · {signature(sorted[0]!)} · {sorted[0]!.unit}
        <br />
        蓝色：预测；橙色：实际观测。只绘制返回行；缺口不连接，未展示完整原始观测序列。
      </figcaption>
      <p>图表保留原生字号。Tab 聚焦下方图表视口，使用左右方向键横向查看时间轴。</p>
      <div
        className="nm-historical-chart-viewport"
        data-historical-curve-viewport
        role="region"
        aria-label="历史预测曲线，可用左右方向键横向滚动"
        tabIndex={0}
      >
        <svg
          viewBox="0 0 920 250"
          width="920"
          height="250"
          role="img"
          aria-label="当前页预测与实际观测曲线"
        >
          <line x1="50" y1="200" x2="880" y2="200" stroke="#b4c2d5" />
          <text x="5" y="40">
            {numeric(hi)}
          </text>
          <text x="5" y="190">
            {numeric(lo)}
          </text>
          {(['predicted', 'actual'] as const).map((key, k) => (
            <g key={key} fill={k ? '#a8490c' : '#1a5cc8'} stroke={k ? '#a8490c' : '#1a5cc8'}>
              {sorted.map((r, i) => {
                const v = r[key];
                const prev = sorted[i - 1];
                return v === null ? null : (
                  <g key={i}>
                    {prev &&
                      prev[key] !== null &&
                      Date.parse(r.targetAt) - Date.parse(prev.targetAt) === 3600000 && (
                        <line x1={x(prev)} y1={y(prev[key])} x2={x(r)} y2={y(v)} />
                      )}
                    <circle cx={x(r)} cy={y(v)} r="3">
                      <title>{`${key} ${r.targetAt}: ${v} ${r.unit}`}</title>
                    </circle>
                  </g>
                );
              })}
            </g>
          ))}
          <text x="50" y="230">
            {sorted[0]!.targetAt}
          </text>
          <text x="500" y="248">
            {sorted[sorted.length - 1]!.targetAt}
          </text>
        </svg>
      </div>
    </figure>
  );
}
export function HistoricalRows({ data }: { data: HistoricalPageResult<HistoricalRow> }) {
  return (
    <>
      <p>
        匹配 {data.total} 行 · 当前页 {data.items.length} 行。inputEnd ≤ origin；actual
        在独立评价阶段读取。
      </p>
      <HistoricalLedgerCurve rows={data.items} />
      <div
        className="nm-table-wrap"
        role="region"
        aria-label="历史实验数据表，可横向滚动"
        tabIndex={0}
      >
        <table className="nm-table">
          <caption>分页原始预测 / 目标观测 / 不可评估依据</caption>
          <thead>
            <tr>
              <th>对象与起点</th>
              <th>目标时刻 / +小时</th>
              <th>预测 / 实际 / signed error</th>
              <th>评价状态 / paired</th>
              <th>不可变输入窗口</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r, i) => (
              <tr key={i} data-historical-ledger-row={`${r.origin}|${r.targetAt}|${r.method}`}>
                <td>
                  {r.stationId} / {r.metric}
                  <small>
                    {r.method} · {r.split}
                  </small>
                  {r.origin}
                </td>
                <td>
                  {r.targetAt}
                  <small>+{r.horizonHours}h</small>
                </td>
                <td>
                  {numeric(r.predicted)} / {numeric(r.actual)} /{' '}
                  {r.status === 'EVALUATED' && r.predicted !== null && r.actual !== null
                    ? numeric(r.predicted - r.actual)
                    : '不可评估'}
                  <small>{r.unit}</small>
                </td>
                <td>
                  {r.status} / {r.paired ? '是' : '否'}
                  <small>{r.reason || '无已记录原因'}</small>
                </td>
                <td>
                  {r.inputStart || '未知'} → {r.inputEnd || '未知'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data.items.length && <p className="nm-empty">当前筛选无预测行；不补造结果。</p>}
    </>
  );
}
function Pagination({
  filter,
  total,
  navigate,
}: {
  filter: HistoricalFilter;
  total: number;
  navigate: (f: HistoricalFilter) => void;
}) {
  return (
    <div className="nm-pagination">
      <button
        disabled={filter.offset === 0}
        onClick={() => navigate({ ...filter, offset: Math.max(0, filter.offset - filter.limit) })}
      >
        上一页
      </button>
      <span>
        第 {Math.floor(filter.offset / filter.limit) + 1} 页 · 共 {total} 条
      </span>
      <button
        disabled={filter.offset + filter.limit >= total || filter.offset + filter.limit > 1000000}
        onClick={() => navigate({ ...filter, offset: filter.offset + filter.limit })}
      >
        下一页
      </button>
    </div>
  );
}
function ReadRows({
  port,
  id,
  filter,
  navigate,
}: {
  port: HistoricalPort;
  id: string;
  filter: HistoricalFilter;
  navigate: (f: HistoricalFilter) => void;
}) {
  const read = useCallback(
    (signal: AbortSignal) => port.rows(id, filter, signal),
    [port, id, filter],
  );
  const { state, retry } = useHistoricalRead(read);
  return (
    <section className="nm-management-card">
      <h2>原始样本与分页曲线</h2>
      <HistoricalReadNotice status={state.status} error={state.error} retry={retry} />
      {state.status === 'READY' && state.data && (
        <>
          <HistoricalRows data={state.data} />
          <Pagination filter={filter} total={state.data.total} navigate={navigate} />
        </>
      )}
    </section>
  );
}
function Detail({
  port,
  id,
  filter,
  navigate,
}: {
  port: HistoricalPort;
  id: string;
  filter: HistoricalFilter;
  navigate: (f: HistoricalFilter) => void;
}) {
  const read = useCallback((signal: AbortSignal) => port.run(id, signal), [port, id]);
  const { state, retry } = useHistoricalRead(read);
  const run = state.data;
  const viewFilter = useMemo(
    () => (run ? historicalViewFilter(run, filter) : filter),
    [run, filter],
  );
  return (
    <>
      <HistoricalReadNotice status={state.status} error={state.error} retry={retry} />
      {state.status === 'READY' && run && (
        <>
          <p className="nm-provenance">
            <span data-historical-run-id={run.runId}>runId {run.runId}</span> · 工程运行状态{' '}
            {run.status}
          </p>
          <button type="button" onClick={retry}>
            重新读取历史实验
          </button>
          <HistoricalProvenance run={run} />
          <HistoricalGroups
            run={run}
            filter={filter}
            scope={navigate}
            select={(g) =>
              navigate({
                ...filter,
                stationId: g.stationId,
                metric: g.metric,
                method: g.method,
                horizonHours: String(g.horizonHours),
                from:
                  g.split === 'HELD_OUT'
                    ? run.split.heldoutStart
                    : g.split === 'VALIDATION'
                      ? run.split.validationStart
                      : run.split.trainStart,
                to:
                  g.split === 'HELD_OUT'
                    ? run.split.heldoutEnd
                    : g.split === 'VALIDATION'
                      ? run.split.validationEnd
                      : run.split.trainEnd,
                offset: 0,
              })
            }
          />
          <HistoricalErrorCases
            key={JSON.stringify(filter)}
            run={run}
            filter={filter}
            navigate={navigate}
          />
          <ReadRows port={port} id={id} filter={viewFilter} navigate={navigate} />
        </>
      )}
    </>
  );
}
function RunListRead({
  port,
  filter,
  areaId,
  navigate,
}: {
  port: HistoricalPort;
  filter: HistoricalFilter;
  areaId: string;
  navigate: (f: HistoricalFilter) => void;
}) {
  const read = useCallback((signal: AbortSignal) => port.runs(filter, signal), [port, filter]);
  const { state, retry } = useHistoricalRead(read);
  return (
    <>
      <HistoricalReadNotice status={state.status} error={state.error} retry={retry} />
      {state.status === 'READY' && state.data && (
        <>
          <button type="button" onClick={retry}>
            重新读取历史实验
          </button>
          <HistoricalRunList data={state.data} areaId={areaId} />
          <Pagination filter={filter} total={state.data.total} navigate={navigate} />
        </>
      )}
    </>
  );
}
function browserLocation() {
  return parseHistoricalLocation(
    typeof window === 'undefined' ? HISTORICAL_ROOT : window.location.pathname,
    typeof window === 'undefined' ? '' : window.location.search,
  );
}
export function HistoricalPage({ port: injected }: { readonly port?: HistoricalPort }) {
  const port = useMemo(
    () => injected ?? createHistoricalAdapter({ baseOrigin: apiBaseOrigin() }),
    [injected],
  );
  const [location, setLocation] = useState(browserLocation);
  const [draft, setDraft] = useState(location.filter);
  const [error, setError] = useState('');
  useEffect(() => {
    const restore = () => {
      const next = browserLocation();
      setLocation(next);
      setDraft(next.filter);
      setError('');
    };
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);
  const navigate = (filter: HistoricalFilter) => {
    window.history.pushState(null, '', historicalHref(location.id ?? '', filter, location.areaId));
    setLocation({ ...location, filter });
    setDraft(filter);
    setError('');
  };
  let valid = location.valid;
  try {
    historicalQuery(location.filter);
  } catch {
    valid = false;
  }
  return (
    <div className="p01 nm-page nm-vs04-page nm-historical">
      <PlatformHeader activePage="monitoring" />
      <main id="nm-main" tabIndex={-1} className="nm-vs04-main">
        <p className="nm-vs04-breadcrumb">
          国家级 AI 消防平台 / 常态运行 / 模型效果 / 历史离线实验
        </p>
        <h1>{location.id ? '历史实验详情' : '历史离线实验'}</h1>
        <p className="nm-vs04-disclaimer">
          HISTORICAL_OFFLINE / RESEARCH_EVALUATION / NON-OPERATIONAL / NOT FOR RESCUE USE
        </p>
        <p>
          逐小时历史回测 · 全站点实验室。导航区域 {location.areaId || '全部区域'}{' '}
          不裁切气象站数据。原合成秒级预测与本实验分别评价。
        </p>
        <nav className="nm-vs04-nav" aria-label="历史实验导航">
          <a href={externalHref('', undefined, location.areaId)}>跨地区稳定性验证</a>
          <a href={riskHref('/evaluation', location.areaId)}>← 返回模型效果</a>
          <a href={historicalHref('', undefined, location.areaId)}>全部历史实验</a>
        </nav>
        {!valid ? (
          <section role="alert" className="nm-management-card">
            <h2>链接或筛选无效</h2>
            <p>runId、站点、指标、UTC 时间或分页参数不符合历史查询契约。请从实验列表重新进入。</p>
          </section>
        ) : (
          <div className="nm-management">
            <section className="nm-management-card">
              <h2>只读查询</h2>
              <form
                className="nm-filters"
                onSubmit={(e) => {
                  e.preventDefault();
                  try {
                    historicalQuery(draft);
                    navigate({ ...draft, offset: 0 });
                  } catch {
                    setError('请检查筛选：时间需 UTC ISO 格式，起始不晚于结束。');
                  }
                }}
              >
                <label>
                  站点 ID
                  <input
                    value={draft.stationId}
                    onChange={(e) => setDraft({ ...draft, stationId: e.target.value })}
                  />
                </label>
                <label>
                  指标
                  <select
                    value={draft.metric}
                    onChange={(e) => setDraft({ ...draft, metric: e.target.value })}
                  >
                    <option value="">全部指标</option>
                    <option value="TEMPERATURE">温度 / degC</option>
                    <option value="WIND_SPEED">风速 / m/s</option>
                  </select>
                </label>
                <label>
                  预测目标
                  <select
                    value={draft.horizonHours}
                    onChange={(e) => setDraft({ ...draft, horizonHours: e.target.value })}
                  >
                    <option value="">+1 / +3 / +6 小时</option>
                    {[1, 3, 6].map((h) => (
                      <option value={h} key={h}>
                        +{h} 小时
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  比较方法
                  <select
                    value={draft.method}
                    onChange={(e) => setDraft({ ...draft, method: e.target.value })}
                  >
                    <option value="">四种方法</option>
                    {HISTORICAL_METHODS.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                </label>
                {(['from', 'to'] as const).map((key) => (
                  <label key={key}>
                    {key === 'from' ? '目标开始 UTC（含）' : '目标结束 UTC（不含）'}
                    <input
                      placeholder="2024-10-02T00:00:00Z"
                      value={draft[key]}
                      onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                    />
                  </label>
                ))}
                <button type="submit">应用只读筛选</button>
              </form>
              {error && <p role="alert">{error}</p>}
              <p>筛选只查询已保存数据，不运行训练、不改写结果、不切换当前预测模型。</p>
            </section>
            {location.id ? (
              <Detail
                key={location.id}
                port={port}
                id={location.id}
                filter={location.filter}
                navigate={navigate}
              />
            ) : (
              <RunListRead
                port={port}
                filter={location.filter}
                areaId={location.areaId}
                navigate={navigate}
              />
            )}
          </div>
        )}
        <footer className="nm-vs04-footer">
          P2-NO-VS05 · retrospective hourly research · 来源与数值以已保存实验为准
        </footer>
      </main>
    </div>
  );
}
